import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { useLazyGetVehicleWarningsQuery } from '@/services/warningsApi';
import { LoadingIndicator } from '../app/LoadingIndicator';
import Card from './Card';
import { Criticality, VehicleWarning } from './utils/types';

interface WarningListProps {
    assetId: string;
    initialWarnings: VehicleWarning[];
    total: number;
    isLoading: boolean;
    isExpanded: boolean;
    criticalityFilter: Criticality[] | undefined;
}

const WarningList = ({
    assetId,
    initialWarnings,
    total,
    isLoading,
    isExpanded,
    criticalityFilter,
}: WarningListProps) => {
    const [loadMoreWarnings, setLoadMoreWarnings] = useState<VehicleWarning[]>([]);
    const [loadMore, { isLoading: isLoadingMore }] = useLazyGetVehicleWarningsQuery();

    useEffect(() => {
        setLoadMoreWarnings([]);
    }, [criticalityFilter]);

    const warningsList = [...initialWarnings, ...loadMoreWarnings];
    const hasMore = isExpanded && warningsList.length < total;

    const handleLoadMore = async () => {
        const result = await loadMore({
            assetId,
            limit: 10,
            offset: warningsList.length,
            criticality: criticalityFilter,
        }).unwrap();

        setLoadMoreWarnings((prev) => [...prev, ...result.items]);
    };

    if (isLoading) {
        return <LoadingIndicator />;
    }

    return (
        <>
            <div className="display-flex flex-column divider-y" data-testid="warning-cards-list">
                {warningsList.map((warning, index) => (
                    <Card key={`${warning.type}-${warning.warning_code}-${index}`} warning={warning} />
                ))}
            </div>

            {hasMore && (
                <div className="display-flex justify-content-center margin-bottom-20">
                    <a
                        role="button"
                        tabIndex={0}
                        className={'display-inline-flex align-items-center gap-5 text-color-primary cursor-pointer'}
                        onClick={handleLoadMore}
                    >
                        {isLoadingMore ? (
                            <LoadingIndicator />
                        ) : (
                            <>
                                <span className="rioglyph rioglyph-download text-size-16" />
                                <FormattedMessage id="intl-msg:vehicle-warnings.loadMore" />
                            </>
                        )}
                    </a>
                </div>
            )}
        </>
    );
};

export default WarningList;
